import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import FormSection from './form-section'
import PaymentSection from './payment-section'
import TextInput from '../inputs/text-input'
import SelectInput from '../inputs/select-input'
import NumberInput from '../inputs/number-input'
import PhotoInput from '../inputs/photo-input'
import RichTextInput from '../inputs/rich-text-input'
import FlashAlert from '../../../components/flash-alert'
import { ajaxGet, patchForm, postForm } from '../../../ajax'

export default function LeagueForm({id, onClose, onCreate, onUpdate}) {
  const [league, setLeague] = useState(null)
  const [options, setOptions] = useState(null)
  const defaultValues = { 
    skill_level: 2,
    min_players: 2,
    max_players: 2, 
    teams_count: 2,
    payment_type: 'registration',
    team_reg_cost: 0,
    individual_reg_cost: 0,
    game_cost: 0,
    gender: 'male',
  }
  const { register, reset, control, handleSubmit, setValue, getValues, formState: { errors } } = useForm(
    { defaultValues: defaultValues }
  )
  const [alert, setAlert] = useState(null)
  const [fileLoaded, setFileLoaded] = useState(false)
  const [loading, setLoading] = useState(true)
  const isNew = !id
  const photoUrl = getValues('photo_url')
  const requiredMsg = 'Is required'
  
  useEffect(() => {
    loadLeague()
  }, [])

  const initForm = (data) => {
    for (const [key, value] of Object.entries(data)) { setValue(key, value) }
  }

  const loadLeague = (locationId = null) => {
    const basePath = id ? `/admins/leagues/${id}/edit.json` : '/admins/leagues/new.json'
    const path = locationId ? `${basePath}?location_id=${locationId}` : basePath
    setLoading(true)
    ajaxGet(path)
    .then(response => { if (response.ok) return response.json() })
    .then((json) => { 
      setLeague(json.form)
      initForm(json.form)
      setLoading(false)
      setOptions(json.options)
    })
  }

  const prepareFormData = (data) => {
    let formData = new FormData()
    const permittedData = _.omit(data, 'id', 'day', 'start_date', 'deadline', 'location_name', 'team_price', 'currency')
    for (const key in permittedData) {
      if (permittedData[key] != null) {
        const value = (key == 'name' ? permittedData[key].trim() : permittedData[key])
        formData.append(`league[${key}]`, value)
      }
    }
    return formData
  }

  const onSubmit = (data) => {
    setLoading(true)

    let formData = prepareFormData(data)
    const submitUrl = isNew ? '/admins/leagues.json' : `/admins/leagues/${id}.json`
    const submitMethod = isNew ? postForm : patchForm

    submitMethod(submitUrl, formData)
    .then(response => { return response.json() })
    .then((json) => {
      if (json.alert)
        setAlert(json.alert)
      else {
        const newLeague = json.league
        isNew ? onCreate(newLeague) : onUpdate(newLeague)
      }
    })
  }

  const loadFile = (file) => {
    const fr = new FileReader()
    fr.onloadend = function() { 
      setValue('photo_url', fr.result)
      setFileLoaded(file.name)
    }
    fr.readAsDataURL(file)
  }

  const fileSelected = (file) => {
    if (file.size > 10000000) return window.alert('File size limit is 10MB')    
    if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')

    loadFile(file)
  }

  const validateName = (value) => {
    if (!value.trim()) return requiredMsg
  }

  return <Modal container={document.body} backdrop='static' show={true} onHide={onClose} dialogClassName='league-form-modal'>
    <Modal.Header closeButton>
      <Modal.Title>{isNew ? 'Create League' : 'Edit League'}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      { loading && <div className='animated-spinner'></div> }
      { alert && <FlashAlert text={alert.text} type={alert.type} onHide={() => setAlert(null)} />}
      { league && options &&
        <form className={'league-form' + (loading ? ' isLoading' : '')} onSubmit={handleSubmit(onSubmit)}>
          <FormSection title='General Info' />
          <div className='general-info'>
            <TextInput
              {...register('name', { required: requiredMsg, validate: validateName })}
              label='League Name'
              error={errors.name}
              onChange={(e) => setValue('name', e.target.value)}
            />
            <SelectInput
              {...register('location_id')}
              label='Location'
              options={options.locations}
              onChange={(e) => loadLeague(e.target.value)}
            />
          </div>
          <FormSection title='Match Info' />
          <div className='match-info'>
            <SelectInput
              {...register('field_type_id')}
              label='Game Size'
              options={options.sizes}
              onChange={(e) => setValue('field_type_id', e.target.value)}
            />
            <NumberInput
              {...register('min_players', { 
                required: requiredMsg, 
                min: { value: 2, message: 'Must be >= 2' } 
              })}
              label='Min Players Per Team'
              error={errors.min_players}
              onChange={(e) => setValue('min_players', e.target.value)}
            />
            <NumberInput
              {...register('max_players', { 
                required: requiredMsg, 
                min: { value: 2, message: 'Must be >= 2' } 
              })}
              label='Max Players Per Team'
              error={errors.max_players}
              onChange={(e) => setValue('max_players', e.target.value)}
            />
            <NumberInput
              {...register('teams_count', { 
                required: requiredMsg, 
                min: { value: 2, message: 'Must be >= 2' } 
              })}
              label='# of Teams'
              error={errors.teams_count}
              onChange={(e) => setValue('teams_count', e.target.value)}
            />
            <SelectInput
              {...register('skill_level')}
              label='Skill Level'
              options={options.skill_levels}
              onChange={(e) => setValue('skill_level', e.target.value)}
            />
            <SelectInput
              {...register('gender')}
              label='Gender'
              options={options.genders}
              onChange={(e) => setValue('gender', e.target.value)}
            />
          </div>
          <FormSection title='Set Payment' />
          <PaymentSection
            register={register}
            control={control}
            getValues={getValues}
            setValue={setValue}
            errors={errors}
          />
          <FormSection title='Featured Photo' />
          <PhotoInput photoUrl={photoUrl} onFileLoad={fileSelected} />
          <FormSection title='About this League' />
          <RichTextInput control={control} />
          <div className='row'>
            <div className='col-xs-12 form-buttons'>
              <button className='form-button cancel-button' onClick={(e) => { e.preventDefault(); onClose() }}>Cancel</button>
              <input type='submit' value={isNew ? 'Create' : 'Update'} className='form-button create-button' />
            </div>
          </div>
        </form>
      }
    </Modal.Body>
  </Modal>
}
